import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as ozService from '@/services/payrollEngine.service';

const QUERY_KEYS = {
  payrollRuns: 'oz-payroll-runs',
  payrollRun: 'oz-payroll-run',
  companyConfig: 'oz-company-config',
  employeeProfiles: 'oz-employee-profiles',
  shadowReport: 'oz-shadow-report',
};

export function usePayrollRuns(companyId, limit = 20, offset = 0) {
  return useQuery({
    queryKey: [QUERY_KEYS.payrollRuns, companyId, limit, offset],
    queryFn: () => ozService.getPayrollRuns(companyId, limit, offset),
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

export function usePayrollRun(runId) {
  return useQuery({
    queryKey: [QUERY_KEYS.payrollRun, runId],
    queryFn: () => ozService.getPayrollRun(runId),
    enabled: !!runId,
    staleTime: 30_000,
  });
}

export function useCompanyConfig(companyId) {
  return useQuery({
    queryKey: [QUERY_KEYS.companyConfig, companyId],
    queryFn: () => ozService.getCompanyConfig(companyId),
    enabled: !!companyId,
    retry: false,
  });
}

export function useEmployeeProfiles(companyId) {
  return useQuery({
    queryKey: [QUERY_KEYS.employeeProfiles, companyId],
    queryFn: () => ozService.getEmployeeProfiles(companyId),
    enabled: !!companyId,
  });
}

export function useShadowReport(runId) {
  return useQuery({
    queryKey: [QUERY_KEYS.shadowReport, runId],
    queryFn: () => ozService.getShadowReport(runId),
    enabled: !!runId,
    retry: false,
  });
}

export function useRunPayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ozService.runPayroll,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRuns],
      });
    },
  });
}

export function useCompareShadow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ozService.compareShadow,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRun, variables.runId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.shadowReport, variables.runId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRuns],
      });
    },
  });
}

export function useUploadGroundTruth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ozService.uploadGroundTruth,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRun, variables.runId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.shadowReport, variables.runId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRuns],
      });
    },
  });
}

export function useDeletePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ozService.deletePayrollRun,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.payrollRuns],
      });
    },
  });
}

export function useUpdateCompanyConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, config }) =>
      ozService.updateCompanyConfig(companyId, config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.companyConfig, variables.companyId],
      });
    },
  });
}

export function useUpdateEmployeeProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, profile }) =>
      ozService.updateEmployeeProfile(employeeId, profile),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.employeeProfiles],
      });
    },
  });
}

export function useBulkUpdateProfiles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ozService.bulkUpdateProfiles,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.employeeProfiles],
      });
    },
  });
}
